import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '@/components/layout/Header';
import ProfileHeader from '@/components/profile/ProfileHeader';
import { Card, CardContent, CardHeader, CardFooter, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Calendar as CalendarIcon, Music, Upload, Share2, Globe, Info, Plus } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';

const ReleaseEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isNewRelease = id === 'new';
  const [date, setDate] = React.useState<Date | undefined>(new Date());
  const [activeTab, setActiveTab] = useState("details");
  const { toast } = useToast();
  
  const handleSave = () => {
    toast({
      title: isNewRelease ? "Release created" : "Release updated",
      description: isNewRelease 
        ? "Your new release has been created successfully" 
        : "Your release has been updated successfully",
    });
  };
  
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-24 pb-28 px-4 max-w-3xl mx-auto">
        <ProfileHeader 
          title={isNewRelease ? "Create New Release" : "Edit Release"} 
          description={isNewRelease 
            ? "Upload your music and share it with the world" 
            : "Update your release information"}
          showBackButton
        />
        
        <Tabs 
          defaultValue="details" 
          value={activeTab} 
          onValueChange={setActiveTab}
          className="w-full mb-6"
        >
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="details">Release Details</TabsTrigger>
            <TabsTrigger value="tracks">Tracks</TabsTrigger>
            <TabsTrigger value="distribution">Distribution</TabsTrigger>
          </TabsList>
          
          <TabsContent value="details" className="mt-6">
            <Card className="neo-card animate-slide-up">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Music className="h-5 w-5 text-muted-foreground" />
                  Release Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6">
                  <div>
                    <Label className="block mb-2">Cover Art</Label>
                    <div className="bg-muted aspect-square rounded-md overflow-hidden border border-border flex items-center justify-center">
                      <Upload className="h-8 w-8 text-muted-foreground opacity-50" />
                    </div>
                    <Button variant="outline" size="sm" className="mt-2 w-full">
                      Upload Image
                    </Button>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">Release Title</Label>
                      <Input id="title" placeholder="Enter release title" />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="type">Release Type</Label>
                      <select id="type" className="w-full h-10 px-3 py-2 bg-background border border-input rounded-md">
                        <option value="single">Single</option>
                        <option value="ep">EP</option>
                        <option value="album">Album</option>
                      </select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Release Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !date && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {date ? format(date, "PPP") : <span>Pick a date</span>}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={date}
                            onSelect={setDate}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea 
                    id="description" 
                    placeholder="Describe your release" 
                    className="min-h-[100px]"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Platforms</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {['Spotify', 'Apple Music', 'Amazon', 'YouTube Music', 'SoundCloud', 'Tidal'].map(platform => (
                      <div key={platform} className="flex items-center space-x-2">
                        <input 
                          type="checkbox" 
                          id={platform} 
                          className="h-4 w-4 rounded border-gray-300"
                        />
                        <Label htmlFor={platform} className="font-normal">
                          {platform}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button variant="outline" onClick={() => setActiveTab("tracks")}>
                  Cancel
                </Button>
                <Button onClick={() => setActiveTab("tracks")}>Next: Add Tracks</Button>
              </CardFooter>
            </Card>
          </TabsContent>
          
          <TabsContent value="tracks" className="mt-6">
            <Card className="neo-card animate-slide-up">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Music className="h-5 w-5 text-muted-foreground" />
                  Track Management
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="border border-border rounded-md p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-medium">Track 1</div>
                      <Button variant="outline" size="sm">Remove</Button>
                    </div>
                    <div className="grid gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="track1-title">Track Title</Label>
                        <Input id="track1-title" placeholder="Enter track title" />
                      </div>
                      <div className="flex gap-3">
                        <div className="space-y-2 flex-1">
                          <Label htmlFor="track1-duration">Duration (seconds)</Label>
                          <Input id="track1-duration" type="number" min="1" placeholder="Duration in seconds" />
                        </div>
                        <div className="space-y-2 flex-1">
                          <Label htmlFor="track1-position">Position</Label>
                          <Input id="track1-position" type="number" min="1" value="1" readOnly />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="track1-file">Audio File</Label>
                        <div className="flex gap-2">
                          <Input id="track1-file" type="file" className="flex-1" />
                          <Button variant="outline" size="icon">
                            <Upload className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <Button className="w-full gap-2" variant="outline">
                    <Plus className="h-4 w-4" />
                    <span>Add Another Track</span>
                  </Button>
                </div>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button variant="outline" onClick={() => setActiveTab("details")}>
                  Back: Release Details
                </Button>
                <Button onClick={() => setActiveTab("distribution")}>Next: Distribution</Button>
              </CardFooter>
            </Card>
          </TabsContent>
          
          <TabsContent value="distribution" className="mt-6">
            <Card className="neo-card animate-slide-up">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Share2 className="h-5 w-5 text-muted-foreground" />
                  Distribution Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-muted p-4 rounded-md flex items-start gap-3">
                  <Info className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium mb-1">Distribution Ready</p>
                    <p className="text-muted-foreground">
                      Your release will be prepared for distribution to digital platforms. 
                      You'll be able to select specific platforms and track your distribution status.
                    </p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Distribution Service</Label>
                    <select className="w-full h-10 px-3 py-2 bg-background border border-input rounded-md">
                      <option value="">Select a service...</option>
                      <option value="tunecore">TuneCore</option>
                      <option value="distrokid">DistroKid</option>
                      <option value="cdbaby">CD Baby</option>
                    </select>
                  </div>
                  
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="platforms">
                      <AccordionTrigger>Target Platforms</AccordionTrigger>
                      <AccordionContent>
                        <div className="grid grid-cols-2 gap-3 mt-2">
                          {[
                            'Spotify', 'Apple Music', 'Amazon Music', 'YouTube Music', 
                            'TikTok', 'Instagram', 'Tidal', 'Deezer', 'Pandora'
                          ].map(platform => (
                            <div key={platform} className="flex items-center space-x-2">
                              <input 
                                type="checkbox" 
                                id={`dist-${platform}`} 
                                className="h-4 w-4 rounded border-gray-300"
                              />
                              <Label htmlFor={`dist-${platform}`} className="font-normal">
                                {platform}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                    
                    <AccordionItem value="metadata">
                      <AccordionTrigger>Additional Metadata</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-3 mt-2">
                          <div className="space-y-2">
                            <Label htmlFor="primary-genre">Primary Genre</Label>
                            <select id="primary-genre" className="w-full h-10 px-3 py-2 bg-background border border-input rounded-md">
                              <option value="">Select genre...</option>
                              <option value="pop">Pop</option>
                              <option value="rock">Rock</option>
                              <option value="hiphop">Hip-Hop/Rap</option>
                              <option value="electronic">Electronic</option>
                              <option value="jazz">Jazz</option>
                              <option value="classical">Classical</option>
                              <option value="rnb">R&B/Soul</option>
                              <option value="country">Country</option>
                            </select>
                          </div>
                          
                          <div className="space-y-2">
                            <Label htmlFor="language">Primary Language</Label>
                            <select id="language" className="w-full h-10 px-3 py-2 bg-background border border-input rounded-md">
                              <option value="en">English</option>
                              <option value="es">Spanish</option>
                              <option value="fr">French</option>
                              <option value="de">German</option>
                              <option value="zh">Chinese</option>
                              <option value="ja">Japanese</option>
                              <option value="ko">Korean</option>
                              <option value="pt">Portuguese</option>
                            </select>
                          </div>
                          
                          <div className="space-y-2">
                            <Label htmlFor="explicit">Explicit Content</Label>
                            <select id="explicit" className="w-full h-10 px-3 py-2 bg-background border border-input rounded-md">
                              <option value="no">No explicit content</option>
                              <option value="yes">Contains explicit content</option>
                            </select>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                    
                    <AccordionItem value="royalties">
                      <AccordionTrigger>Royalty Splits</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-3 mt-2">
                          <p className="text-sm text-muted-foreground mb-2">
                            Define how royalties should be split between collaborators:
                          </p>
                          
                          <div className="border border-border rounded-md p-3">
                            <div className="flex justify-between items-center mb-2">
                              <Label htmlFor="primary-artist" className="font-medium">Primary Artist</Label>
                              <Input id="primary-artist-percent" type="number" min="0" max="100" value="100" className="w-20" />
                            </div>
                            <Input id="primary-artist" value="Alex Turner" readOnly />
                          </div>
                          
                          <Button variant="outline" size="sm" className="gap-2">
                            <Plus className="h-4 w-4" />
                            <span>Add Collaborator</span>
                          </Button>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                  
                  <div className="pt-4">
                    <Label className="block mb-2">Distribution Schedule</Label>
                    <div className="flex items-center gap-2">
                      <input 
                        type="radio" 
                        id="dist-immediate" 
                        name="dist-schedule" 
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      <Label htmlFor="dist-immediate" className="font-normal">Distribute as soon as possible</Label>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <input 
                        type="radio" 
                        id="dist-release-date" 
                        name="dist-schedule" 
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      <Label htmlFor="dist-release-date" className="font-normal">Distribute on release date</Label>
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button variant="outline" onClick={() => setActiveTab("tracks")}>
                  Back: Tracks
                </Button>
                <Button onClick={handleSave} className="gap-2">
                  <Globe className="h-4 w-4" />
                  <span>Save & Complete</span>
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default ReleaseEdit;
